import LoadingScreen from "components/LoadingScreen";
import { VersionNotifier } from "components/VersionNotifier";
import useAuth from "hooks/useAuth";
import { useNotificationVersion } from "hooks/useNotificationVersion";
import AuthLayout from "layouts/AuthLayout";
import { WhistleblowingModal } from "pages/customTemplates/DesigualTemplate/components/WhistleblowingModal";
import EmailPageContainer from "pages/email_send_page/skeleton/EmailPageContainer";
import { ReactNode, useEffect } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "redux/store";

type AuthGuardProps = {
  children: ReactNode;
};

export default function AuthGuard({ children }: AuthGuardProps) {
  const { allowed, isLoading } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const onboarding = useSelector((state: any) => state.onboarding);
  // poll server version and return flag when newer build is available
  const hasNewVersion = useNotificationVersion();

  const isOnboardingDone = onboarding?.isOnBoardingStatus;
  const approvalStatus = onboarding?.approvalStatus;

  const token = localStorage.getItem("token");

  useEffect(() => {
    if (isOnboardingDone && approvalStatus === 1) {
      const supplierId = pathname.split("/")[1];
      navigate(`/${supplierId}/documents`, { replace: true });
    }
  }, [isOnboardingDone, approvalStatus]);

  if (isLoading || isOnboardingDone === undefined) {
    return <LoadingScreen />;
  }

  if (!allowed || !token) {
    return (
      <AuthLayout>
        <EmailPageContainer />
      </AuthLayout>
    );
  }

  const supplierId = pathname.split("/")[1];
  const onboardingPath = `/${supplierId}/onboarding`;
  const documentsPath = `/${supplierId}/documents`;
  const accountPath = `/${supplierId}/account`;

  let content: ReactNode;
  if (pathname === accountPath) {
    content = children;
  } else if (isOnboardingDone && pathname === onboardingPath) {
    content = <Navigate to={documentsPath} replace />;
  } else if (!isOnboardingDone && pathname === documentsPath) {
    content = <Navigate to={onboardingPath} replace />;
  } else {
    content = (
      <>
        {/* notify user when a newer version/build is available */}
        <VersionNotifier isOpen={hasNewVersion} />
        {children}
      </>
    );
  }

  return (
    <>
      <WhistleblowingModal />
      {content}
    </>
  );
}
